import { useDroppable } from "@dnd-kit/core";
import { Badge, Flex, Text } from "@radix-ui/themes";
import { motion } from "framer-motion";
import cx from "classnames";
import { useMemo, useCallback, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { TrackerStageIcon } from "@/v2/assets/mor-shapes/TrackerStageIcon";
import TrackerItemCard from "@/features/tracker/TrackerItemCard";
import {
  TrackerFieldType,
  TrackerColumn,
  TrackerItem,
  Tracker,
} from "@/v2/entities/tracker/Tracker.model";
import styles from "../trackerDetail.module.scss";
import { Sources } from "@/v2/entities/tracker/Tracker.api";

interface VirtualTrackerColumnProps {
  column: TrackerColumn;
  trackerItems: TrackerItem[];
  trackerId: string;
  onItemClick: (interactionId: string, intercationType: Sources) => void;
  onItemUpdate: () => void;
  trackerColumns: TrackerColumn[];
  trackerFields: TrackerFieldType[];
  isLoading?: boolean;
  tracker?: Tracker;
}

// Estimated height for tracker items (in pixels)
// Increased to prevent overlapping - will be measured dynamically
const ESTIMATED_ITEM_HEIGHT = 180;

function TrackerStageTitle({
  column,
  itemsCount,
}: {
  column: TrackerColumn;
  itemsCount: number;
}) {
  // Only allow valid stages for the icon
  const allowedStage =
    column.stage === "to_do" ||
    column.stage === "in_progress" ||
    column.stage === "done"
      ? column.stage
      : "to_do";
  return (
    <Flex pb={"4"} className="items-center">
      <TrackerStageIcon stage={allowedStage} width={12} height={12} />
      <span className="text-md font-medium ml-2">{column.name}</span>
      <Badge color="gray" variant="soft" size="1" className="ml-2">
        {itemsCount}
      </Badge>
    </Flex>
  );
}

function VirtualTrackerColumn({
  column,
  trackerItems,
  trackerId,
  onItemClick,
  onItemUpdate,
  trackerColumns,
  trackerFields,
  isLoading = false,
  tracker,
}: VirtualTrackerColumnProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: column.id,
  });

  // Create ref for the scrollable container
  const parentRef = useRef<HTMLDivElement>(null);

  // Filter items for this column
  const filteredItems = useMemo(
    () => trackerItems?.filter((item) => item.columnId === column.id) ?? [],
    [trackerItems, column.id]
  );

  const isCapturedColumn =
    column.name.toLowerCase().includes("captured") || column.stage === "to_do";

  // Initialize the virtualizer with dynamic sizing
  const rowVirtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT,
    overscan: 5,
    measureElement: (element) =>
      element?.getBoundingClientRect().height ?? ESTIMATED_ITEM_HEIGHT,
  });

  // Render function for virtual list items
  const renderTrackerItem = useCallback(
    (item: TrackerItem) => (
      <div className="pb-3">
        <TrackerItemCard
          trackerId={trackerId}
          item={item}
          onItemClick={onItemClick}
          onItemUpdate={onItemUpdate}
          trackerColumns={trackerColumns}
          trackerFields={trackerFields}
        />
      </div>
    ),
    [trackerId, onItemClick, onItemUpdate, trackerColumns, trackerFields]
  );

  // Create dynamic message based on tracker sources
  const getEmptyStateMessage = () => {
    if (!tracker?.sources || tracker.sources.length === 0) {
      return {
        title: "Waiting for matching interactions...",
        subtitle:
          "New interactions that match your criteria will automatically appear here",
      };
    }

    const sources = tracker.sources;
    let title = "Waiting for matching interactions...";
    let subtitle = "";

    if (sources.length === 1) {
      const source = sources[0];
      subtitle = `New ${source} that match your criteria will automatically appear here`;
    } else if (sources.length === 2) {
      subtitle = `New ${sources[0]} and ${sources[1]} that match your criteria will automatically appear here`;
    } else {
      subtitle =
        "New interactions that match your criteria will automatically appear here";
    }

    return { title, subtitle };
  };

  const emptyStateMessage = getEmptyStateMessage();

  return (
    <motion.div
      key={column.id}
      className={cx(styles.trackerDetailColumn, {
        [styles.trackerDetailColumn__over]: isOver,
      })}
      ref={setNodeRef}
      animate={{
        scale: isOver ? 1.02 : 1,
        backgroundColor: isOver ? "var(--gray-4)" : "var(--gray-3)",
      }}
      transition={{ duration: 0.2 }}
    >
      <div className={styles.trackerDetailColumn__header}>
        <TrackerStageTitle column={column} itemsCount={filteredItems.length} />
      </div>

      <div className={styles.trackerDetailColumn__content}>
        {isLoading ? (
          <div className={styles.trackerDetailColumn__skeletonItems}>
            <div className={styles.trackerDetailColumn__skeletonItem} />
            <div className={styles.trackerDetailColumn__skeletonItem} />
            <div className={styles.trackerDetailColumn__skeletonItem} />
          </div>
        ) : filteredItems.length > 0 ? (
          // Virtual scrolling for performance with large lists using @tanstack/react-virtual
          <div ref={parentRef} className="h-full min-h-0 flex-1 overflow-auto">
            <div
              className="relative w-full"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                  className="absolute top-0 left-0 w-full"
                  style={{
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {renderTrackerItem(filteredItems[virtualItem.index])}
                </div>
              ))}
            </div>
          </div>
        ) : isCapturedColumn ? (
          // Empty state for captured column
          <motion.div
            className={styles.trackerDetailColumn__emptyState}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                <span className="text-gray-400 text-xs">📋</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text
                  size="2"
                  color="gray"
                  highContrast
                  align="center"
                  weight="medium"
                >
                  {emptyStateMessage.title}
                </Text>
              </div>
              <Text size="2" color="gray" align="center">
                {emptyStateMessage.subtitle}
              </Text>
            </div>
          </motion.div>
        ) : null}

        {/* Drop zone indicator when dragging over empty column */}
        {isOver && filteredItems.length === 0 && !isCapturedColumn && (
          <motion.div
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="text-gray-600 font-medium">Drop here</div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export { VirtualTrackerColumn };
export type { VirtualTrackerColumnProps };
